/**
 * The adoption link — the phone's way in (`HEADLESS-HUB-ONBOARDING.md` §3, D5).
 *
 * A phone cannot do mDNS (no NSD plugin in the Capacitor shell yet), so it
 * cannot *discover* an unclaimed hub the way the desktop does. The sticker on
 * the box carries the two things a phone needs and nothing else:
 *
 *     torik://adopt?h=<hubPublicId>&u=http://<hostname>.local:<port>
 *     https://torik.ai/adopt?h=…&u=…          (the scheme-less fallback)
 *
 * `h` is F9's hub public id — an **identity, never an address**. `u` is the
 * address to try. That split is the whole safety property of this link: the
 * address is a hint (a `.local` name that may or may not resolve, an IP that a
 * DHCP lease may hand to somebody else tomorrow), while the identity is what
 * the hub itself will confirm. So nothing is adopted until the answer to
 * `GET /api/hub/identity` carries the same `publicId` — see
 * `matchAdoptLinkToHub`. That is why the payload does not need to be secret:
 * scanning somebody else's sticker gets you a refusal, not a hub.
 *
 * Deliberately NOT here: the enrollment link's `c=`/`k=` fields. Adoption has
 * no code to spend — the adopting account is the credential — and a payload
 * that carries one would invite exactly the "type this" flow this feature
 * exists to delete. `parseAdoptLinkDetailed` accepts `h` + `u` only.
 *
 * Nothing in this module touches the network, storage or Vue: the fetch is
 * `services/hubAdoption.ts`, the rendering is `components/hub/AdoptHubSheet.vue`.
 */
import { HUB_PUBLIC_ID_PATTERN, normalizeHubPublicId, parseHubEndpoint } from './hubIdentity';

/**
 * How a scanned string relates to the **enrollment** link this app used to read.
 *
 * Kept as the honest three-valued answer a sticker parser has to give about a
 * frame it did not recognise: `other` and `empty` are different refusals.
 */
export type ScannedCodeKind =
  /** A string no shape here recognises. */
  | 'other'
  /** Nothing decoded. */
  | 'empty';

/** Custom scheme for the adoption QR (the enrollment link's twin). */
export const ADOPT_LINK_SCHEME = 'torik';

/** The path both encodings share. */
export const ADOPT_LINK_PATH = '/adopt';

/** Fallback origin for clients that cannot handle a custom scheme. */
export const ADOPT_LINK_HTTPS_ORIGIN = 'https://torik.ai';

/** What a sticker QR carries, once parsed. */
export interface AdoptLink {
  /** F9 hub public id (`h=`), lowercased — the value the hub must confirm. */
  hubPublicId: string;
  /** The address hint (`u=`), as it was written in the link. */
  lanUrl: string;
  /** `lanUrl` broken into the host and port a request can be built from. */
  host: string;
  port: number;
}

/**
 * Build the QR payload the hub's sticker is printed from.
 *
 * The hub half of this card family prints it too (host-side publisher); this
 * is the same string, built from the app side so the two can be asserted
 * equal in one test instead of drifting.
 */
export function buildAdoptLink(payload: { hubPublicId: string; lanUrl: string }): { uri: string; https: string } {
  const query = [`h=${encodeURIComponent(payload.hubPublicId)}`, `u=${encodeURIComponent(payload.lanUrl)}`].join('&');

  return {
    uri: `${ADOPT_LINK_SCHEME}://adopt?${query}`,
    https: `${ADOPT_LINK_HTTPS_ORIGIN}${ADOPT_LINK_PATH}?${query}`,
  };
}

export interface AdoptLinkParseOk {
  ok: true;
  kind: 'adopt-scheme' | 'https-adopt';
  payload: AdoptLink;
}

export interface AdoptLinkParseFailure {
  ok: false;
  /** `empty` — nothing decoded; `unrelated` — a QR that was never ours;
   *  `malformed` — ours by shape, incomplete or carrying a bad id/address. */
  reason: 'empty' | 'unrelated' | 'malformed';
  kind: ScannedCodeKind | 'adopt-scheme' | 'https-adopt';
}

export type AdoptLinkParse = AdoptLinkParseOk | AdoptLinkParseFailure;

/** Which of the three shapes this string is, before validity is judged. */
export function classifyAdoptLink(raw: unknown): 'adopt-scheme' | 'https-adopt' | 'other' | 'empty' {
  if (typeof raw !== 'string') return 'empty';
  const value = raw.trim();
  if (!value) return 'empty';
  if (new RegExp(`^${ADOPT_LINK_SCHEME}://adopt\\?`, 'i').test(value)) return 'adopt-scheme';
  if (/^https?:\/\/[^/]+\/adopt\?/i.test(value)) return 'https-adopt';
  return 'other';
}

/**
 * Is this string an adoption link at all? Answers with the *textual* verdict
 * only (no parsing), because the two callers ask it before they know whether
 * the payload is complete: the scanner decides which flow a decoded frame
 * belongs to, and the enroll surface must not swallow an adoption link as
 * "not an enrollment code".
 */
export function isAdoptLink(raw: unknown): boolean {
  return classifyAdoptLink(raw) !== 'other' && classifyAdoptLink(raw) !== 'empty';
}

/**
 * Parse either encoding of the adoption link.
 *
 * Both `h` and `u` are required, and `h` must be F9's public id shape: a link
 * whose id is mangled cannot be checked against the hub, and adopting without
 * that check is the one thing this link must never allow. `u` must be an
 * http(s) address that yields a host and a valid port — a payload with a
 * path-only or portless-garbage address is `malformed`, not "use it anyway".
 */
export function parseAdoptLinkDetailed(raw: unknown): AdoptLinkParse {
  const kind = classifyAdoptLink(raw);
  if (kind === 'empty') return { ok: false, reason: 'empty', kind: 'empty' };
  if (kind === 'other') return { ok: false, reason: 'unrelated', kind: 'other' };

  const value = (raw as string).trim();
  const query = value.slice(value.indexOf('?') + 1);
  const params = new URLSearchParams(query);

  const hubPublicId = normalizeHubPublicId(params.get('h'));
  const lanUrl = (params.get('u') ?? '').trim();

  if (!hubPublicId || !lanUrl) return { ok: false, reason: 'malformed', kind };
  if (!/^https?:\/\//i.test(lanUrl)) return { ok: false, reason: 'malformed', kind };

  const endpoint = parseHubEndpoint(lanUrl, 443);
  if (!endpoint) return { ok: false, reason: 'malformed', kind };

  return {
    ok: true,
    kind,
    payload: { hubPublicId, lanUrl, host: endpoint.host, port: endpoint.port },
  };
}

/** The payload, or `null` — the shape most callers want. */
export function parseAdoptLink(raw: unknown): AdoptLink | null {
  const parsed = parseAdoptLinkDetailed(raw);
  return parsed.ok ? parsed.payload : null;
}

/**
 * How a scanned adoption link relates to the hub answering at its address.
 *
 *   - `ok` — the hub named itself and the id matches: adopt it.
 *   - `wrong-hub` — it named a *different* hub, or the link's id is not one of
 *     F9's public ids at all. Refused; nothing is sent.
 *   - `unverifiable` — nothing answered `GET /api/hub/identity` at that
 *     address (an address that drifted, a hub too old to serve the route, a
 *     hub whose key file is unreadable). Adoption is refused here on purpose:
 *     the public id is the only thing that makes a sticker's address safe to
 *     trust, so without it the app would be dialling a guess.
 */
export type AdoptLinkMatch = 'ok' | 'wrong-hub' | 'unverifiable';

export function matchAdoptLinkToHub(
  link: { hubPublicId: string } | null | undefined,
  hubPublicId: string | null | undefined,
): AdoptLinkMatch {
  const claimed = normalizeHubPublicId(link?.hubPublicId);
  if (!claimed) return 'wrong-hub';
  const answered = normalizeHubPublicId(hubPublicId);
  if (!answered) return 'unverifiable';
  return claimed === answered ? 'ok' : 'wrong-hub';
}

/** Does this string look like a hub public id? Re-exported for the UI's own checks. */
export const ADOPT_LINK_PUBLIC_ID_PATTERN = HUB_PUBLIC_ID_PATTERN;
